import { useState, useRef, useEffect } from 'react';
import { Box, Button, CircularProgress, Typography, makeStyles } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import Dialog from '@material-ui/core/Dialog';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import { dismissNotifications, readNotifications } from 'src/requests/notifications';
import { enqueueSnackbar } from 'notistack';

const SEEK_THRESHOLD = 2;
const WATCHED_THRESHOLD = 0.95;
const ERROR_TIMEOUT = 3000;

const VideoOverlay = ({ data, id }: { data: any; id: string }) => {
  const classes = useStyles();
  const [isOpen, setIsOpen] = useState(true);
  const [error, setError] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [isVideoFullyWatched, setIsVideoFullyWatched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastTimeRef = useRef(0);
  const totalWatchedTimeRef = useRef(0);

  useEffect(() => () => timeoutRef.current && clearTimeout(timeoutRef.current), []);

  const checkIfVideoFullyWatched = (totalWatched: number, duration: number) =>
    duration > 0 && totalWatched / duration >= WATCHED_THRESHOLD;

  const resetTracking = () => {
    totalWatchedTimeRef.current = 0;
    setIsVideoFullyWatched(false);
  };

  const detectSeek = (currentTime: number) => {
    const timeDiff = Math.abs(currentTime - lastTimeRef.current);
    if (timeDiff > SEEK_THRESHOLD) {
      resetTracking();
      return true;
    }
    return false;
  };

  const handleTimeUpdate = () => {
    const video = videoRef.current;
    if (!video) return;

    const currentTime = video.currentTime;
    if (detectSeek(currentTime)) {
      lastTimeRef.current = currentTime;
      return;
    }

    if (currentTime > lastTimeRef.current) {
      totalWatchedTimeRef.current += currentTime - lastTimeRef.current;
    }

    setIsVideoFullyWatched(checkIfVideoFullyWatched(totalWatchedTimeRef.current, video.duration));
    lastTimeRef.current = currentTime;
  };

  const handleSeeked = () => {
    const video = videoRef.current;
    if (!video) return;
    detectSeek(video.currentTime);
    lastTimeRef.current = video.currentTime;
  };

  const handleClick = async () => {
    if (isVideoFullyWatched) {
      setIsLoading(true);
      try {
        await readNotifications(id, data.notificationId);
        await dismissNotifications(id, data.notificationId);
      } finally {
        setIsOpen(false);
        setIsLoading(false);
      }
    } else {
      setError(true);
      timeoutRef.current = setTimeout(() => {
        setError(false);
        timeoutRef.current = null;
      }, ERROR_TIMEOUT);
    }
  };

  const handleVideoError = (event: any) => {
    setVideoError(true);
    const errorMsg = event?.target?.error?.message;
    console.error('Video error', errorMsg);
    enqueueSnackbar(`Video error: ${errorMsg}`, { variant: 'error' });
  };

  return (
    <Dialog open={isOpen} maxWidth="md" fullWidth>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <DialogTitle>
          {data?.title || 'Video Instruction'} -{' '}
          <span className={error ? classes.errorText : classes.text}>
            You must watch the entire video to close this notification,{' '}
            <i>you can't skip to the end of the video</i>.
          </span>
        </DialogTitle>

        {isLoading ? (
          <Box style={{ marginRight: 25 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Button variant="contained" onClick={handleClick} className={classes.closeButton}>
            <CloseIcon />
          </Button>
        )}
      </Box>

      {videoError || !data?.downloadUrl ? (
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
          style={{ paddingBottom: 20 }}
        >
          <Typography>
            There was an error with the video, you can close this notification and we will try again
            later.
          </Typography>

          <Button variant="contained" onClick={handleClick} className={classes.closeButton}>
            Close
          </Button>
        </Box>
      ) : (
        <DialogContent dividers>
          <Box className={classes.videoContainer}>
            <video
              ref={videoRef}
              src={data.downloadUrl}
              controls
              className={classes.video}
              onError={handleVideoError}
              onTimeUpdate={handleTimeUpdate}
              onSeeked={handleSeeked}
            />
          </Box>

          {data?.content && <Typography style={{ paddingTop: 10 }}>{data?.content}</Typography>}
        </DialogContent>
      )}
    </Dialog>
  );
};

export default VideoOverlay;

const useStyles = makeStyles(() => ({
  closeButton: {
    backgroundColor: 'red',
    color: 'white',
    marginRight: 25,
    '&:hover': {
      backgroundColor: 'red',
      opacity: '0.9',
    },
  },
  videoContainer: {
    height: '60vh',
    width: '100%',
    margin: '0 auto',
  },
  video: {
    width: '100%',
    height: '100%',
    borderRadius: 8,
    background: '#000',
  },
  errorText: {
    color: 'red',
    fontSize: 15,
  },
  text: {
    color: 'black',
    fontSize: 14,
    transition: 'all 0.3s ease-out',
  },
}));
